/**
 * 飞书（Feishu/Lark）相关工具
 * 网页应用免登文档：
 * https://open.feishu.cn/document/quickly-create-a-login-free-web-app/introduction
 */

// 按需引入请求层（避免在工具函数顶部静态依赖，减少循环引用风险）
import * as request from '@/utils/request'

// 默认授权地址（浏览器打开时展示飞书登录页，含扫码）
const DEFAULT_AUTHORIZE_URL = 'https://accounts.feishu.cn/open-apis/authen/v1/authorize'
// 默认扫码登录页地址（用于页面内嵌入二维码 iframe）
const DEFAULT_SCAN_URL = 'https://open.feishu.cn/connect/qrconnect/page/sso'
// 飞书客户端内 H5 JS-SDK（用于静默获取免登授权码）
const DEFAULT_H5_SDK_URL = 'https://lf1-cdn-tos.bytegoofy.com/goofy/lark/op/h5-js-sdk-1.5.24.js'

// 判断是否在飞书客户端内（飞书 / Lark 内置浏览器 UA 均包含 Lark）
export function isFeishuClient() {
  const ua = window.navigator.userAgent || ''
  return ua.indexOf('Lark') > -1 || ua.indexOf('Feishu') > -1
}

/**
 * 构造查询串（跳过空值）
 * @param {Object} params
 * @returns {string}
 */
function buildQuery(params = {}) {
  const parts = []
  Object.keys(params).forEach((key) => {
    const value = params[key]
    if (value === undefined || value === null || value === '') {
      return
    }
    parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
  })
  return parts.join('&')
}

/**
 * 构造飞书免登授权地址
 * @param {string} clientId 飞书应用 App ID（cli_ 开头）
 * @param {string} redirectUri 授权回调地址（需在飞书开放平台配置）
 * @param {string} state 状态参数（原样回传，可用于校验）
 * @param {string} scope 授权范围（可选，多个用空格分隔）
 * @param {string} baseUrl 授权地址（可选，默认取内置地址）
 * @returns {string} 授权地址
 */
export function getFeishuAuthUrl(clientId, redirectUri, state, scope, baseUrl) {
  const query = buildQuery({
    client_id: clientId,
    redirect_uri: redirectUri,
    response_type: 'code',
    state: state || 'feishu',
    scope: scope || ''
  })
  return `${baseUrl || DEFAULT_AUTHORIZE_URL}?${query}`
}

/**
 * 构造飞书扫码登录页地址（用于页面内嵌入二维码 iframe）
 * @param {string} appId 飞书应用 App ID
 * @param {string} redirectUri 授权回调地址
 * @param {string} state 状态参数
 * @param {string} scope 授权范围（可选）
 * @param {string} baseUrl 扫码页面地址（可选，默认取内置地址）
 * @returns {string} 扫码页地址
 */
export function getFeishuScanUrl(appId, redirectUri, state, scope, baseUrl) {
  const query = buildQuery({
    app_id: appId,
    redirect_uri: redirectUri,
    state: state || 'feishu',
    scope: scope || ''
  })
  return `${baseUrl || DEFAULT_SCAN_URL}?${query}`
}

let sdkLoading = null

/**
 * 动态加载飞书 H5 JS-SDK
 * @param {string} sdkUrl SDK 地址（可选）
 * @returns {Promise<void>}
 */
export function loadFeishuH5Sdk(sdkUrl) {
  if (window.h5sdk && window.tt) {
    return Promise.resolve()
  }
  if (sdkLoading) {
    return sdkLoading
  }
  sdkLoading = new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.src = sdkUrl || DEFAULT_H5_SDK_URL
    script.onload = () => resolve()
    script.onerror = () => {
      sdkLoading = null
      reject(new Error('飞书 JS-SDK 加载失败'))
    }
    document.head.appendChild(script)
  })
  return sdkLoading
}

/**
 * 在飞书客户端内静默获取免登授权码（JSAPI）
 * @param {string} appId 飞书应用 App ID
 * @param {Function} callback 成功回调，入参为 code
 * @param {Function} fail 失败回调
 * @param {string} sdkUrl SDK 地址（可选）
 */
export function getFeishuCodeFromClient(appId, callback, fail, sdkUrl) {
  loadFeishuH5Sdk(sdkUrl)
    .then(() => {
      if (!window.h5sdk || !window.tt || typeof window.tt.requestAuthCode !== 'function') {
        fail && fail(new Error('当前环境不支持飞书免登（JSAPI 不存在）'))
        return
      }
      const run = () => {
        try {
          window.tt.requestAuthCode({
            appId: appId,
            success: (res) => {
              if (res && res.code) {
                callback && callback(res.code)
              } else {
                fail && fail(new Error('未获取到飞书免登授权码'))
              }
            },
            fail: (err) => {
              fail && fail(err)
            }
          })
        } catch (e) {
          fail && fail(e)
        }
      }
      if (typeof window.h5sdk.ready === 'function') {
        window.h5sdk.ready(run)
      } else {
        run()
      }
    })
    .catch((err) => {
      fail && fail(err)
    })
}

/**
 * 在现有 URL 上追加/覆盖查询参数（用于生成飞书回调地址）
 * @param {string} url 原始地址
 * @param {Object} params 需要设置的参数
 * @returns {string} 新的地址
 */
export function appendQueryParams(url, params = {}) {
  const [base, hash = ''] = url.split('#')
  const [path, search = ''] = base.split('?')
  const searchParams = new URLSearchParams(search)
  Object.keys(params).forEach((key) => {
    if (params[key] === undefined || params[key] === null) {
      searchParams.delete(key)
    } else {
      searchParams.set(key, params[key])
    }
  })
  const query = searchParams.toString()
  return `${path}${query ? `?${query}` : ''}${hash ? `#${hash}` : ''}`
}

/**
 * 在星象仪前端同步飞书角标（打开星象仪页面且处于飞书环境时调用）。
 *
 * 背景：星象仪(cli_aa28) 与 OA/报销(cli_aa20) 是两个独立飞书应用但用户一致。
 * 采用「两端实时互相同步」策略：后端 sync-badge 会把 cli_aa28 与 cli_aa20 两个应用
 * 都写为同一个最新 OA 待办数；这里再按后端返回的 badge_num 用客户端
 * tt.updateBadge(count) 即时刷新当前设备上下文。整段失败仅告警，绝不阻断星象仪业务。
 *
 * @returns {Promise<boolean>} 是否成功同步了一次
 */
export function syncFeishuBadge() {
  if (!isFeishuClient()) {
    return Promise.resolve(false)
  }
  return Promise.resolve()
    .then(() => request.post('/feishu/sync-badge', {}))
    .then((res) => {
      // 后端回带实际角标数（badge_num），取到就用它做客户端即时刷新
      const badgeNum = (res && typeof res === 'object' && res.badge_num !== undefined && res.badge_num !== null)
        ? Number(res.badge_num)
        : 0
      return { badgeNum, serverNum: badgeNum }
    })
    .then((ctx) => loadFeishuH5Sdk().then(() => ctx))
    .then((ctx) => {
      if (!window.h5sdk || !window.tt || typeof window.tt.updateBadge !== 'function') {
        // 环境不支持客户端角标：服务端已同步到两个 appid，工作台角标下次刷新即正确
        return false
      }
      // 取当前页 h5sdk.config 签名（去掉 #fragment，与后端 explode('#') 对齐）
      const pageUrl = window.location.href.split('#')[0]
      return request.get('/feishu/jsapi-config', { url: pageUrl }).then((config) => ({ config, ctx }))
    })
    .then((next) => {
      if (!next || !next.config || typeof next.config !== 'object') {
        return false
      }
      const config = next.config
      const badgeNum = next.ctx.badgeNum
      return new Promise((resolve) => {
        try {
          window.h5sdk.config({
            appId: config.app_id,
            timestamp: config.timestamp,
            nonceStr: config.nonceStr,
            signature: config.signature,
            jsApiList: ['updateBadge'],
            onSuccess: () => {
              // 官方时序坑：h5sdk.config 成功后不能立刻调 updateBadge，需延时约 1000ms
              setTimeout(() => {
                try {
                  window.tt.updateBadge({
                    badgeNum: badgeNum,
                    success: () => resolve(true),
                    fail: () => resolve(false)
                  })
                } catch (e) {
                  resolve(false)
                }
              }, 1000)
            },
            onFail: () => resolve(false)
          })
        } catch (e) {
          resolve(false)
        }
      })
    })
    .catch((err) => {
      console.warn('[feishu] 同步星象仪角标失败（不阻断）:', err)
      return false
    })
}
